/**
 * SwapHistory Component
 *
 * Enriched history view of all swap operations with:
 * - Timeline filterable by period
 * - Search by token
 * - Tag display for cross-chain swaps
 * - Detailed view with chain information
 * - Performance tracking from origin
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
import { getSwapChain } from '../../utils/costBasisTracking';
import type { Token, Position, Prices } from '../../types';

interface SwapHistoryProps {
  tokens: Token[];
  prices: Prices;
  isExpertMode?: boolean;
  onExport?: () => void;
}

interface SwapHistoryItem {
  id: string;
  date: string;
  fromToken: string;
  fromTokenId: string;
  toToken: string;
  toTokenId: string;
  fromQuantity: number;
  toQuantity: number;
  fromValueUsd: number;
  toValueUsd: number;
  currentValueUsd: number;
  originalCostBasis: number;
  performanceVsSwap: number;
  performanceVsOrigin: number;
  isCrossChain: boolean;
  sourceChain?: string;
  destChain?: string;
  bridgeProtocol?: string;
  swapPlatform?: string;
  fees: number;
  swapChainId?: string;
  note?: string;
  position: Position;
}

type FilterPeriod = 'all' | '7d' | '30d' | '90d' | '1y';

const CHAIN_COLORS: Record<string, string> = {
  ethereum: '#627EEA',
  bsc: '#F0B90B',
  polygon: '#8247E5',
  arbitrum: '#28A0F0',
  optimism: '#FF0420',
  avalanche: '#E84142',
  base: '#0052FF',
  fantom: '#1969FF',
  solana: '#9945FF',
  cosmos: '#2E3148',
};

export function SwapHistory({ tokens, prices, isExpertMode = false, onExport }: SwapHistoryProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [showCrossChainOnly, setShowCrossChainOnly] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState<SwapHistoryItem | null>(null);

  // Build swap history from tokens
  const swapHistory = useMemo<SwapHistoryItem[]>(() => {
    const items: SwapHistoryItem[] = [];

    tokens.forEach(token => {
      token.positions
        .filter(p => p.is_swap === true)
        .forEach(position => {
          // Find source token from swap chain
          let fromToken = 'Unknown';
          let fromTokenId = '';
          let fromQuantity = 0;
          let fromValueUsd = 0;

          if (position.swap_chain_id) {
            const chain = getSwapChain(position, tokens);
            const currentIndex = chain.findIndex(p => p.id === position.id);
            if (currentIndex > 0) {
              const prevPosition = chain[currentIndex - 1];
              const prevToken = tokens.find(t =>
                t.positions.some(p => p.id === prevPosition.id)
              );
              if (prevToken) {
                fromToken = prevToken.displayName || prevToken.id;
                fromTokenId = prevToken.id;
                fromQuantity = prevPosition.quantity;
                fromValueUsd = prevPosition.quantity * prevPosition.price;
              }
            }
          }

          const currentPrice = prices[token.id]?.usd || position.price;
          const toValueUsd = position.quantity * position.price;
          const currentValueUsd = position.quantity * currentPrice;
          const originalCost = position.original_cost_basis
            ? position.quantity * position.original_cost_basis
            : toValueUsd;

          const performanceVsSwap = toValueUsd > 0
            ? ((currentValueUsd - toValueUsd) / toValueUsd) * 100
            : 0;
          const performanceVsOrigin = originalCost > 0
            ? ((currentValueUsd - originalCost) / originalCost) * 100
            : 0;

          const isCrossChain = !!(position.source_chain && position.destination_chain &&
            position.source_chain !== position.destination_chain);
          const fees = (position.bridge_fee || 0) + (position.gas_fee_usd || 0);

          items.push({
            id: position.id,
            date: position.date,
            fromToken,
            fromTokenId,
            toToken: token.displayName || token.id,
            toTokenId: token.id,
            fromQuantity,
            toQuantity: position.quantity,
            fromValueUsd,
            toValueUsd,
            currentValueUsd,
            originalCostBasis: originalCost,
            performanceVsSwap,
            performanceVsOrigin,
            isCrossChain,
            sourceChain: position.source_chain,
            destChain: position.destination_chain,
            bridgeProtocol: position.bridge_protocol,
            swapPlatform: position.swap_platform,
            fees,
            swapChainId: position.swap_chain_id,
            note: position.note,
            position,
          });
        });
    });

    // Sort by date descending
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [tokens, prices]);

  // Apply filters
  const filteredHistory = useMemo(() => {
    let filtered = swapHistory;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.fromToken.toLowerCase().includes(query) ||
        item.toToken.toLowerCase().includes(query) ||
        item.swapPlatform?.toLowerCase().includes(query) ||
        item.bridgeProtocol?.toLowerCase().includes(query)
      );
    }

    // Period filter
    if (filterPeriod !== 'all') {
      const now = new Date();
      let cutoff: Date;
      switch (filterPeriod) {
        case '7d':
          cutoff = new Date(now.setDate(now.getDate() - 7));
          break;
        case '30d':
          cutoff = new Date(now.setDate(now.getDate() - 30));
          break;
        case '90d':
          cutoff = new Date(now.setDate(now.getDate() - 90));
          break;
        case '1y':
          cutoff = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
      }
      filtered = filtered.filter(item => new Date(item.date) >= cutoff);
    }

    // Cross-chain filter
    if (showCrossChainOnly) {
      filtered = filtered.filter(item => item.isCrossChain);
    }

    return filtered;
  }, [swapHistory, searchQuery, filterPeriod, showCrossChainOnly]);

  // Don't render if no swaps
  if (swapHistory.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: 'var(--card)',
      borderRadius: '12px',
      border: '1px solid var(--border)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icons.Clock size={20} style={{ color: 'var(--accent)' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 700 }}>
            {t('swapHistory.title', 'Historique des Swaps')}
          </h3>
          <span style={{
            padding: '2px 8px',
            borderRadius: '12px',
            background: 'var(--accent)',
            color: 'white',
            fontSize: '11px',
            fontWeight: 600,
          }}>
            {filteredHistory.length}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Icons.Search size={14} style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--muted)',
            }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('swapHistory.search', 'Rechercher...')}
              style={{
                paddingLeft: '32px',
                width: '180px',
                fontSize: '13px',
              }}
            />
          </div>

          {/* Period Filter */}
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value as FilterPeriod)}
            aria-label="Filtrer par période"
            style={{ fontSize: '13px', minWidth: '100px' }}
          >
            <option value="all">{t('swapHistory.allTime', 'Tout')}</option>
            <option value="7d">{t('swapHistory.7days', '7 jours')}</option>
            <option value="30d">{t('swapHistory.30days', '30 jours')}</option>
            <option value="90d">{t('swapHistory.90days', '90 jours')}</option>
            <option value="1y">{t('swapHistory.1year', '1 an')}</option>
          </select>

          {/* Cross-chain filter */}
          {isExpertMode && (
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}>
              <input
                type="checkbox"
                checked={showCrossChainOnly}
                onChange={(e) => setShowCrossChainOnly(e.target.checked)}
              />
              Cross-chain
            </label>
          )}

          {/* Export button */}
          {onExport && (
            <button
              onClick={onExport}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                color: 'var(--text)',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Icons.Download size={14} />
              {t('common.export', 'Export')}
            </button>
          )}
        </div>
      </div>

      {/* Swap List */}
      <div style={{ maxHeight: '500px', overflow: 'auto' }}>
        {filteredHistory.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--muted)',
          }}>
            <Icons.Search size={32} style={{ opacity: 0.5, marginBottom: '12px' }} />
            <p>{t('swapHistory.noResults', 'Aucun swap trouvé')}</p>
          </div>
        ) : (
          filteredHistory.map((item, index) => (
            <div
              key={item.id}
              onClick={() => setSelectedSwap(selectedSwap?.id === item.id ? null : item)}
              style={{
                padding: '16px 20px',
                borderBottom: index < filteredHistory.length - 1 ? '1px solid var(--border)' : 'none',
                cursor: 'pointer',
                background: selectedSwap?.id === item.id ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
                transition: 'background 0.2s',
              }}
            >
              {/* Main Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
              }}>
                {/* Left: Swap info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  {/* Date */}
                  <div style={{
                    width: '60px',
                    fontSize: '11px',
                    color: 'var(--muted)',
                    textAlign: 'center',
                  }}>
                    <div>{new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</div>
                    <div>{new Date(item.date).getFullYear()}</div>
                  </div>

                  {/* Swap Direction */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <div style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      background: 'var(--bg-secondary)',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}>
                      {item.fromToken}
                    </div>
                    <Icons.ArrowRight size={16} style={{ color: 'var(--muted)' }} />
                    <div style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      background: 'var(--accent)',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}>
                      {item.toToken}
                    </div>
                  </div>

                  {/* Tags */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {item.isCrossChain && (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 600,
                      }}>
                        BRIDGE
                      </span>
                    )}
                    {item.swapPlatform && (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: 'var(--bg-secondary)',
                        fontSize: '9px',
                        color: 'var(--muted)',
                      }}>
                        {item.swapPlatform}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: Performance */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>
                    ${fmt(item.currentValueUsd, 0)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: item.performanceVsOrigin >= 0 ? '#22c55e' : '#ef4444',
                  }}>
                    {item.performanceVsOrigin >= 0 ? '+' : ''}{fmt(item.performanceVsOrigin, 1)}%
                  </div>
                </div>
              </div>

              {/* Expanded Details (Expert Mode) */}
              {selectedSwap?.id === item.id && isExpertMode && (
                <div style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid var(--border)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px',
                }}>
                  {/* Quantities */}
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Quantité échangée</div>
                    <div style={{ fontSize: '12px' }}>
                      {fmt(item.fromQuantity, 6)} → {fmt(item.toQuantity, 6)}
                    </div>
                  </div>

                  {/* Values */}
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Valeur au swap</div>
                    <div style={{ fontSize: '12px' }}>
                      ${fmt(item.fromValueUsd, 2)} → ${fmt(item.toValueUsd, 2)}
                    </div>
                  </div>

                  {/* Original Cost */}
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Coût d'origine</div>
                    <div style={{ fontSize: '12px' }}>${fmt(item.originalCostBasis, 2)}</div>
                  </div>

                  {/* Fees */}
                  {item.fees > 0 && (
                    <div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Frais</div>
                      <div style={{ fontSize: '12px', color: '#f59e0b' }}>${fmt(item.fees, 2)}</div>
                    </div>
                  )}

                  {/* Chain Info */}
                  {item.isCrossChain && (
                    <>
                      <div>
                        <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Chaînes</div>
                        <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: CHAIN_COLORS[item.sourceChain || ''] || 'var(--bg-secondary)',
                            color: 'white',
                            fontSize: '10px',
                          }}>
                            {item.sourceChain}
                          </span>
                          <Icons.ArrowRight size={12} />
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: CHAIN_COLORS[item.destChain || ''] || 'var(--bg-secondary)',
                            color: 'white',
                            fontSize: '10px',
                          }}>
                            {item.destChain}
                          </span>
                        </div>
                      </div>

                      {item.bridgeProtocol && (
                        <div>
                          <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Bridge</div>
                          <div style={{ fontSize: '12px' }}>{item.bridgeProtocol}</div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Note */}
                  {item.note && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Note</div>
                      <div style={{
                        fontSize: '12px',
                        fontStyle: 'italic',
                        padding: '8px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '4px',
                      }}>
                        {item.note}
                      </div>
                    </div>
                  )}

                  {/* Swap Chain ID */}
                  {item.swapChainId && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '2px' }}>Chain ID</div>
                      <div style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        color: 'var(--muted)',
                      }}>
                        {item.swapChainId}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
